import { useState, useEffect } from 'react'
import { Toolbar, Tool, Primitive } from './Toolbar'
import { Scene3D } from './Scene3D'
import { SceneHierarchy } from './SceneHierarchy'
import { UndoRedoControls } from './UndoRedoControls'
import { Inspector } from './Inspector'
import { useUndoRedo } from '../hooks/useUndoRedo'
import { SceneObject, GeometricProperties } from '../db/service'
import * as THREE from 'three'

export function ModelingApp() {
  const [selectedTool, setSelectedTool] = useState<Tool>('select')
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  
  const {
    objects,
    ready,
    saveState,
    updateObjects,
    undo,
    redo,
    canUndo,
    canRedo,
    currentAction,
    historyLength
  } = useUndoRedo()

  // Clear selection when switching to non-transform tools
  useEffect(() => {
    if (selectedTool !== 'select' && selectedTool !== 'move' && selectedTool !== 'rotate' && selectedTool !== 'scale') {
      const newObjects = objects.map(obj => ({
        ...obj,
        selected: false
      }))
      updateObjects(newObjects)
      setSelectedObjectId(null)
    }
  }, [selectedTool, objects, updateObjects])

  // Validate selectedObjectId when objects array changes (e.g., from undo/redo)
  useEffect(() => {
    if (selectedObjectId && !objects.find(obj => obj.id === selectedObjectId)) {
      console.log('Selected object no longer exists, clearing selection:', selectedObjectId)
      setSelectedObjectId(null)
    }
  }, [objects, selectedObjectId])

  const handleToolSelect = (tool: Tool) => {
    setSelectedTool(tool)
    console.log('Selected tool:', tool)
    
    // Only deselect objects when switching to 'add' tool
    // Keep selection when switching between select/move/rotate
    if (tool === 'add') {
      const newObjects = objects.map(obj => ({ ...obj, selected: false }))
      updateObjects(newObjects)
      setSelectedObjectId(null)
    } else {
      // Failsafe: Re-select currently selected object for transform tools
      // This ensures transform handles appear on the selected object immediately
      const currentlySelected = objects.find(obj => obj.selected)
      if (currentlySelected && (tool === 'select' || tool === 'move' || tool === 'rotate' || tool === 'scale')) {
        setSelectedObjectId(currentlySelected.id)
      }
    }
  }

  const handlePrimitiveAdd = (primitive: Primitive) => {
    console.log('Adding primitive:', primitive)
    
    // Get the highest order for root objects
    const rootObjects = objects.filter(obj => !obj.parentId)
    const maxOrder = rootObjects.length > 0 ? Math.max(...rootObjects.map(obj => obj.order || 0)) : -1
    
    // Set default geometric properties based on primitive type
    const getDefaultProperties = (type: Primitive): GeometricProperties => {
      switch (type) {
        case 'sphere':
          return { sphereSegments: 16, sphereRings: 16 }
        case 'cylinder':
          return { cylinderRadialSegments: 16, cylinderHeightSegments: 1 }
        case 'cone':
          return { coneRadialSegments: 8, coneHeightSegments: 1 }
        case 'cube':
          return { cubeWidthSegments: 1, cubeHeightSegments: 1, cubeDepthSegments: 1 }
        case 'plane':
          return { planeWidthSegments: 1, planeHeightSegments: 1 }
        default:
          return {}
      }
    }
    
    const newObject: SceneObject = {
      id: `${primitive}-${crypto.randomUUID()}`,
      name: `${primitive.charAt(0).toUpperCase() + primitive.slice(1)} ${rootObjects.length + 1}`,
      type: primitive,
      position: rootObjects.length === 0 ? [0, 1, 0] : [
        Math.random() * 4 - 2, // Random position between -2 and 2
        Math.random() * 2 + 1, // Height between 1 and 3
        Math.random() * 4 - 2
      ],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      selected: true,
      parentId: null,
      children: [],
      order: maxOrder + 1,
      properties: getDefaultProperties(primitive)
    }
    
    const newObjects = [...objects.map(object => ({ ...object, selected: false })), newObject]
    setSelectedObjectId(newObject.id)
    setSelectedTool('move')
    saveState(newObjects, `Added ${primitive}`)
    document.dispatchEvent(new CustomEvent("somewhere:analytics", { detail: "object_created" }))
  }

  const handleObjectSelect = (objectId: string | null) => {
    // Allow selection with select, move, rotate, or scale tools
    if (selectedTool === 'select' || selectedTool === 'move' || selectedTool === 'rotate' || selectedTool === 'scale') {
      if (objectId === null) {
        // Deselect all objects
        const newObjects = objects.map(obj => ({
          ...obj,
          selected: false
        }))
        updateObjects(newObjects)
        setSelectedObjectId(null)
        console.log('Deselected all objects')
      } else {
        // Select specific object
        const newObjects = objects.map(obj => ({
          ...obj,
          selected: obj.id === objectId
        }))
        updateObjects(newObjects)
        setSelectedObjectId(objectId)
        console.log('Selected object:', objectId)
      }
    } else {
      // For other tools, clear selection
      const newObjects = objects.map(obj => ({
        ...obj,
        selected: false
      }))
      updateObjects(newObjects)
      setSelectedObjectId(null)
    }
  }

  const handleObjectTransform = (objectId: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => {
    const newObjects = objects.map(obj => 
      obj.id === objectId 
        ? { ...obj, position, rotation, scale }
        : obj
    )
    // Only update objects immediately for smooth UI - no saving during transform
    updateObjects(newObjects)
  }

  const handleTransformComplete = (objectId: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) => {
    const newObjects = objects.map(obj => 
      obj.id === objectId 
        ? { ...obj, position, rotation, scale }
        : obj
    )
    // Save state immediately when transform is complete
    saveState(newObjects, 'Transform complete')
  }

  const handlePropertyChange = (objectId: string, properties: GeometricProperties) => {
    const newObjects = objects.map(obj => 
      obj.id === objectId 
        ? { ...obj, properties }
        : obj
    )
    // Save state immediately when properties change
    saveState(newObjects, 'Property change')
  }

  const handleObjectDelete = (objectId: string) => {
    console.log('Deleting object:', objectId)
    
    // Find the object to delete
    const objectToDelete = objects.find(obj => obj.id === objectId)
    if (!objectToDelete) {
      console.warn('Object to delete not found:', objectId)
      return
    }
    
    // Remove object and clean up hierarchy relationships
    const newObjects = objects.filter(obj => obj.id !== objectId).map(obj => {
      // If this object was a child of the deleted object, make it a root object
      if (obj.parentId === objectId) {
        return {
          ...obj,
          parentId: null,
          order: 0
        }
      }
      
      // If this object had the deleted object as a child, remove it from children array
      if (obj.children && obj.children.includes(objectId)) {
        return {
          ...obj,
          children: obj.children.filter(childId => childId !== objectId)
        }
      }
      
      return obj
    })
    
    console.log('Objects after deletion:', newObjects.length, newObjects.map(obj => ({ id: obj.id, name: obj.name })))
    
    // Clear selection if the deleted object was selected
    if (selectedObjectId === objectId) {
      setSelectedObjectId(null)
    }
    
    saveState(newObjects, `Delete ${objectToDelete.name || objectToDelete.type}`)
  }

  // Add keyboard shortcuts for tools
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (event.target instanceof HTMLInputElement) return

      switch (event.key) {
        case '1':
          event.preventDefault()
          setSelectedTool('select')
          break
        case '2':
          event.preventDefault()
          setSelectedTool('move')
          break
        case '3':
          event.preventDefault()
          setSelectedTool('rotate')
          break
        case '4':
          event.preventDefault()
          setSelectedTool('scale')
          break
        case 'x':
        case 'X':
        case 'Delete':
        case 'Backspace':
          event.preventDefault()
          // Delete the currently selected object
          if (selectedObjectId) {
            handleObjectDelete(selectedObjectId)
          }
          break
      }
      
      // Failsafe: Re-select currently selected object after tool change
      // This ensures transform handles appear on the selected object immediately
      const currentlySelected = objects.find(obj => obj.selected)
      if (currentlySelected && (event.key === '1' || event.key === '2' || event.key === '3' || event.key === '4')) {
        setSelectedObjectId(currentlySelected.id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [objects, selectedObjectId, handleObjectDelete])

  const handleObjectReorder = (draggedId: string, targetId: string | null, position: 'before' | 'after' | 'child') => {
    console.log('handleObjectReorder called:', { draggedId, targetId, position })
    console.log('Objects before reorder:', objects.length, objects.map(obj => ({ id: obj.id, name: obj.name })))
    
    // Helper function to convert world transforms to local transforms relative to parent using Three.js matrices
    const worldToLocalTransform = (
      worldPos: [number, number, number], 
      worldRot: [number, number, number],
      worldScale: [number, number, number],
      parentPos: [number, number, number],
      parentRot: [number, number, number], 
      parentScale: [number, number, number]
    ) => {
      // Create Three.js objects for the world transform
      const worldMatrix = new THREE.Matrix4()
      const worldPosition = new THREE.Vector3(...worldPos)
      const worldRotation = new THREE.Euler(...worldRot)
      const worldScaleVec = new THREE.Vector3(...worldScale)
      
      worldMatrix.compose(worldPosition, new THREE.Quaternion().setFromEuler(worldRotation), worldScaleVec)
      
      // Create Three.js objects for the parent transform
      const parentMatrix = new THREE.Matrix4()
      const parentPosition = new THREE.Vector3(...parentPos)
      const parentRotation = new THREE.Euler(...parentRot)
      const parentScaleVec = new THREE.Vector3(...parentScale)
      
      parentMatrix.compose(parentPosition, new THREE.Quaternion().setFromEuler(parentRotation), parentScaleVec)
      
      // Calculate local transform: localMatrix = parentMatrix^-1 * worldMatrix
      const parentInverse = new THREE.Matrix4().copy(parentMatrix).invert()
      const localMatrix = new THREE.Matrix4().multiplyMatrices(parentInverse, worldMatrix)
      
      // Decompose the local matrix
      const localPosition = new THREE.Vector3()
      const localQuaternion = new THREE.Quaternion()
      const localScale = new THREE.Vector3()
      localMatrix.decompose(localPosition, localQuaternion, localScale)
      
      const localRotation = new THREE.Euler().setFromQuaternion(localQuaternion)
      
      return {
        position: [localPosition.x, localPosition.y, localPosition.z] as [number, number, number],
        rotation: [localRotation.x, localRotation.y, localRotation.z] as [number, number, number],
        scale: [localScale.x, localScale.y, localScale.z] as [number, number, number]
      }
    }
    
    // Helper function to convert local transforms back to world transforms using Three.js matrices
    const localToWorldTransform = (
      localPos: [number, number, number],
      localRot: [number, number, number], 
      localScale: [number, number, number],
      parentPos: [number, number, number],
      parentRot: [number, number, number],
      parentScale: [number, number, number]
    ) => {
      // Create Three.js objects for the local transform
      const localMatrix = new THREE.Matrix4()
      const localPosition = new THREE.Vector3(...localPos)
      const localRotation = new THREE.Euler(...localRot)
      const localScaleVec = new THREE.Vector3(...localScale)
      
      localMatrix.compose(localPosition, new THREE.Quaternion().setFromEuler(localRotation), localScaleVec)
      
      // Create Three.js objects for the parent transform
      const parentMatrix = new THREE.Matrix4()
      const parentPosition = new THREE.Vector3(...parentPos)
      const parentRotation = new THREE.Euler(...parentRot)
      const parentScaleVec = new THREE.Vector3(...parentScale)
      
      parentMatrix.compose(parentPosition, new THREE.Quaternion().setFromEuler(parentRotation), parentScaleVec)
      
      // Calculate world transform: worldMatrix = parentMatrix * localMatrix
      const worldMatrix = new THREE.Matrix4().multiplyMatrices(parentMatrix, localMatrix)
      
      // Decompose the world matrix
      const worldPosition = new THREE.Vector3()
      const worldQuaternion = new THREE.Quaternion()
      const worldScale = new THREE.Vector3()
      worldMatrix.decompose(worldPosition, worldQuaternion, worldScale)
      
      const worldRotation = new THREE.Euler().setFromQuaternion(worldQuaternion)
      
      return {
        position: [worldPosition.x, worldPosition.y, worldPosition.z] as [number, number, number],
        rotation: [worldRotation.x, worldRotation.y, worldRotation.z] as [number, number, number],
        scale: [worldScale.x, worldScale.y, worldScale.z] as [number, number, number]
      }
    }
    
    const newObjects = [...objects]
    const draggedObj = newObjects.find(obj => obj.id === draggedId)
    if (!draggedObj) {
      console.error('CRITICAL: Dragged object not found in objects array!', draggedId)
      console.log('Available objects:', objects.map(obj => ({ id: obj.id, name: obj.name })))
      console.log('This could indicate the object was deleted during drag operation')
      return
    }

    console.log('Before reorder - draggedObj:', draggedObj)

    // Ensure all objects have children arrays initialized
    newObjects.forEach(obj => {
      if (!obj.children) obj.children = []
    })

    if (position === 'child') {
      if (targetId) {
        console.log('Making child relationship')
        
        // Store the current world transforms
        const currentWorldTransforms = {
          position: draggedObj.position,
          rotation: draggedObj.rotation, 
          scale: draggedObj.scale
        }
        
        console.log('Before hierarchy change - world transforms:', currentWorldTransforms)
        
        // Remove from old parent first (convert back to world if needed)
        if (draggedObj.parentId) {
          const oldParent = newObjects.find(obj => obj.id === draggedObj.parentId)
          if (oldParent && oldParent.children) {
            oldParent.children = oldParent.children.filter(id => id !== draggedId)
            // Convert from local back to world coordinates
            const worldTransforms = localToWorldTransform(
              draggedObj.position, draggedObj.rotation, draggedObj.scale,
              oldParent.position, oldParent.rotation, oldParent.scale
            )
            draggedObj.position = worldTransforms.position
            draggedObj.rotation = worldTransforms.rotation
            draggedObj.scale = worldTransforms.scale
            console.log('Converted from local to world:', worldTransforms)
          }
        }
        
        // Make dragged object a child of target
        draggedObj.parentId = targetId
        const targetObj = newObjects.find(obj => obj.id === targetId)
        if (targetObj) {
          if (!targetObj.children) targetObj.children = []
          if (!targetObj.children.includes(draggedId)) {
            targetObj.children.push(draggedId)
          }
          
          // Convert world transforms to local transforms relative to new parent
          const localTransforms = worldToLocalTransform(
            currentWorldTransforms.position, currentWorldTransforms.rotation, currentWorldTransforms.scale,
            targetObj.position, targetObj.rotation, targetObj.scale
          )
          draggedObj.position = localTransforms.position
          draggedObj.rotation = localTransforms.rotation
          draggedObj.scale = localTransforms.scale
          console.log('Converted from world to local relative to new parent:', localTransforms)
          console.log('Target object after update:', targetObj)
        }
        // Set order as the last child
        const siblings = newObjects.filter(obj => obj.parentId === targetId)
        draggedObj.order = siblings.length > 0 ? Math.max(...siblings.map(s => s.order || 0)) + 1 : 0
        console.log('Dragged object after update:', draggedObj)
      } else {
        console.log('Making root object (no target) - preserving world transforms')
        // Remove from old parent and convert to world coordinates
        if (draggedObj.parentId) {
          const oldParent = newObjects.find(obj => obj.id === draggedObj.parentId)
          console.log('Old parent before cleanup:', oldParent)
          if (oldParent && oldParent.children) {
            oldParent.children = oldParent.children.filter(id => id !== draggedId)
            // Convert from local to world coordinates to preserve visual position
            const worldTransforms = localToWorldTransform(
              draggedObj.position, draggedObj.rotation, draggedObj.scale,
              oldParent.position, oldParent.rotation, oldParent.scale
            )
            draggedObj.position = worldTransforms.position
            draggedObj.rotation = worldTransforms.rotation
            draggedObj.scale = worldTransforms.scale
            console.log('Converted to world coordinates to preserve position:', draggedObj.position)
            console.log('Old parent after cleanup:', oldParent)
          }
        }
        draggedObj.parentId = null
        draggedObj.order = 0
      }
    } else if (targetId) {
      // Reorder at same level
      const targetObj = newObjects.find(obj => obj.id === targetId)
      if (!targetObj) return

      // Remove from old parent if needed
      if (draggedObj.parentId) {
        const oldParent = newObjects.find(obj => obj.id === draggedObj.parentId)
        if (oldParent && oldParent.children) {
          oldParent.children = oldParent.children.filter(id => id !== draggedId)
        }
      }

      // Set new parent
      draggedObj.parentId = targetObj.parentId

      // Add to new parent's children if needed
      if (draggedObj.parentId) {
        const newParent = newObjects.find(obj => obj.id === draggedObj.parentId)
        if (newParent) {
          if (!newParent.children) newParent.children = []
          if (!newParent.children.includes(draggedId)) {
            newParent.children.push(draggedId)
          }
        }
      }

      // Reorder siblings
      const siblings = newObjects.filter(obj => obj.parentId === draggedObj.parentId && obj.id !== draggedId)
      siblings.sort((a, b) => (a.order || 0) - (b.order || 0))

      let newOrder = 0
      for (const sibling of siblings) {
        if (sibling.id === targetId) {
          if (position === 'before') {
            draggedObj.order = newOrder
            newOrder++
            sibling.order = newOrder
          } else {
            sibling.order = newOrder
            newOrder++
            draggedObj.order = newOrder
          }
        } else {
          sibling.order = newOrder
        }
        newOrder++
      }
    } else {
      // Move to root level
      if (draggedObj.parentId) {
        const oldParent = newObjects.find(obj => obj.id === draggedObj.parentId)
        if (oldParent && oldParent.children) {
          oldParent.children = oldParent.children.filter(id => id !== draggedId)
        }
      }
      draggedObj.parentId = null
      const rootObjects = newObjects.filter(obj => !obj.parentId && obj.id !== draggedId)
      draggedObj.order = rootObjects.length > 0 ? Math.max(...rootObjects.map(o => o.order || 0)) + 1 : 0
    }

    console.log('Final objects state before saving:', newObjects.map(obj => ({
      id: obj.id,
      name: obj.name,
      parentId: obj.parentId,
      children: obj.children,
      order: obj.order
    })))
    
    console.log('Objects after reorder:', newObjects.length, newObjects.map(obj => ({ id: obj.id, name: obj.name })))
    saveState(newObjects, 'Reorder objects')
  }

  const handleObjectRename = (objectId: string, newName: string) => {
    const newObjects = objects.map(obj => 
      obj.id === objectId 
        ? { ...obj, name: newName }
        : obj
    )
    saveState(newObjects, `Rename object to ${newName}`)
  }

  return (
    <div className="modeling-app">
      <div className="viewport">
        <Scene3D
          objects={objects}
          selectedTool={selectedTool}
          selectedObjectId={selectedObjectId}
          onObjectSelect={handleObjectSelect}
          onObjectTransform={handleObjectTransform}
          onTransformComplete={handleTransformComplete}
        />
        {ready && objects.length === 0 && <div className="first-object">
          <h1>Make your first shape.</h1>
          <p>Add a cube, then drag its handles to move it.</p>
          <button type="button" onClick={() => handlePrimitiveAdd('cube')}>Start with a cube</button>
        </div>}
      </div>
      
      <div className="scene-hierarchy-panel">
        <SceneHierarchy
          objects={objects}
          selectedObjectId={selectedObjectId}
          onObjectSelect={handleObjectSelect}
          onObjectReorder={handleObjectReorder}
          onObjectRename={handleObjectRename}
          onPrimitiveAdd={handlePrimitiveAdd}
        />
      </div>
      
      {selectedObjectId && <div className="inspector-panel">
        <Inspector
          selectedObject={objects.find(obj => obj.id === selectedObjectId) || null}
          onPropertyChange={handlePropertyChange}
        />
      </div>}
      
      <div className="scene-info-panel">
        <div className="scene-info">
          <UndoRedoControls
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            currentAction={currentAction}
            historyLength={historyLength}
          />
        </div>
      </div>
      
      <Toolbar 
        selectedTool={selectedTool}
        onToolSelect={handleToolSelect}
        onPrimitiveAdd={handlePrimitiveAdd}
      />
    </div>
  )
}
